<script lang="ts" setup>
import { ref, computed } from 'vue';
import { BAlert, BButton, BModal, BSpinner } from 'bootstrap-vue-next';
import { useUserStore } from '@/store/user';
import { useQuery } from '@vue/apollo-composable';
import {
  OrganizationDocument,
  UserOrganizationAssociationsDocument,
  OrganizationType,
  OrganizationUserAssociationType,
} from '@/gql/graphql';
import OrganizationForm from '@/components/organization/OrganizationForm.vue';
import OrganizationMembershipList from '@/components/organization/OrganizationMembershipList.vue';
import InviteCreate from '@/components/invite/InviteCreate.vue';
import InviteList from '@/components/invite/InvitesList.vue';
import { ORGANIZATION_USER_ROLES } from '@/constants';
import { deepParseDates } from '@/utils/date-utils';

// user store to get user ID or token-based identity
const userStore = useUserStore();

// invites counts
const inviteCountIncoming = ref(0);
const inviteCountOutgoing = ref(0);

// invites refs
const incomingInvitesListRef = ref<InstanceType<typeof InviteList> | null>(
  null
);
const outgoingInvitesListRef = ref<InstanceType<typeof InviteList> | null>(
  null
);

// refs to handle the modals
const selectedOrgAssoc = ref<OrganizationUserAssociationType | null>(null);
const showOrgDetails = ref(false);

// Show organization details modal
function showOrganizationDetails(
  assoc: OrganizationUserAssociationType,
  index: number,
  event: Event
) {
  selectedOrgAssoc.value = assoc;
  showOrgDetails.value = true;
}

// track org-user association mutation progress
const showInviteCreate = ref(false);

// --- Query user`s organization, user may be owner, member or admin. ---
const {
  result: userOrganizationResult,
  loading: loadingUserOrganization,
  error: errorUserOrganization,
  refetch: refetchOrganization,
} = useQuery(OrganizationDocument);

const userOrgData = computed<OrganizationType | null>(() => {
  console.debug('fetched org:', userOrganizationResult.value?.organization);
  if (userOrganizationResult.value?.organization) {
    return {
      ...(deepParseDates(
        userOrganizationResult.value.organization
      ) as OrganizationType),
    };
  }
  return null;
});

// --- Query organization-user associations ---
const userId = computed(() => userStore.id || null);

// Then define the user-organization associations queries
const {
  result: userAssocResult,
  loading: loadingUserAssoc,
  error: errorUserAssoc,
  refetch: refetchUserAssoc,
} = useQuery(
  UserOrganizationAssociationsDocument,
  () => ({
    userId: Number(userId.value),
  }),
  {
    enabled: computed(() => Boolean(userId.value)),
  }
);

const userAssociations = computed<OrganizationUserAssociationType[]>(() => {
  const rawList = userAssocResult.value?.userOrganizationAssociations || [];
  const parsedList = rawList.map((assoc) => ({
    ...(deepParseDates(assoc) as OrganizationUserAssociationType),
  }));
  console.debug('User associations:', parsedList);
  return parsedList;
});

// --- Organizations "owner" or "admin" can edit ---
const canEditOrg = computed(() => {
  if (!userOrgData.value) {
    return false;
  }
  if (
    userAssociations.value.some(
      (assoc) =>
        assoc.organization?.id === userOrgData.value?.id &&
        (assoc.userRole === ORGANIZATION_USER_ROLES.OWNER ||
          assoc.userRole === ORGANIZATION_USER_ROLES.ADMIN) &&
        !assoc.removedAt
    )
  ) {
    return true;
  }
  return false;
});

/* The array of associations for the user, from orgAssocResult */
const organizationAssociations = computed<OrganizationUserAssociationType[]>(
  () => {
    //const rawList = orgAssocResult.value?.organizationUserAssociations || [];
    const rawList = userOrgData.value?.usersAssociated || [];
    const parsedList = rawList.map((assoc) => ({
      ...(assoc as OrganizationUserAssociationType),
    }));
    console.debug('Organization associations:', parsedList);
    return parsedList;
  }
);

// Membership is "owner", "admin" or "member"
const membership = computed(() => {
  if (!userOrgData.value) return null;
  const assoc = userAssociations.value.find(
    (assoc) => assoc.organization?.id === userOrgData.value?.id
  );
  if (!assoc) return null;
  return assoc.userRole;
});

const noMembership = computed(() => {
  // return membership.value === null;
  return userStore.noOrganizationSelected;
});

// --- Handlers ---
async function handleOrganizationSaved() {
  await requestRefetchOrganization();
  await requestRefetchUserAssoc();
  await userStore.fetchUserDetails();
}

async function requestRefetchOrganization() {
  await refetchOrganization()
    ?.catch((err) => {
      console.error('Refetch organization failed:', err);
    })
    ?.then(() => {
      console.debug('Refetched organization');
    });
}

async function requestRefetchUserAssoc() {
  await refetchUserAssoc()
    ?.catch((err) => {
      console.error('Refetch user-organization associations failed:', err);
    })
    ?.then(() => {
      console.debug('Refetched user-organization associations');
    });
}

// --- handle component emits and necessary updates ---
function updateNumberOfIncomingInvites(count: number) {
  inviteCountIncoming.value = count;
  userStore.setNumberOfPendingInvites(count);
}

function updateNumberOfOutgoingInvites(count: number) {
  inviteCountOutgoing.value = count;
}

async function handleMembershipsUpdate() {
  await requestRefetchOrganization();
  await requestRefetchUserAssoc();
}

function handleIncomingInvitesUpdated() {
  incomingInvitesListRef.value?.requestSearch();
  handleMembershipsUpdate();
}

function handleOutgoingInvitesUpdated() {
  outgoingInvitesListRef.value?.requestSearch();
}
</script>

<template>
  <div>
    <h1>
      <font-awesome-icon :icon="['fas', 'building-user']" class="me-2" />
      Organization Settings
    </h1>
    <p v-if="membership && userOrgData">
      You are currently acting as <strong>{{ membership }}</strong> of the
      <strong>{{ userOrgData.name }}</strong> organization.
    </p>

    <!-- If user has an organization, show form in read/edit mode. If none, show create form. -->
    <!-- Query Error -->
    <b-alert
      v-model="errorUserOrganization"
      variant="danger"
      dismissible
      class="mt-3"
    >
      {{ errorUserOrganization }}
      <b-button
        @click="requestRefetchOrganization"
        variant="outline-dark"
        class="btn-sm"
      >
        <font-awesome-icon :icon="['fas', 'sync']" />
        Retry</b-button
      >
    </b-alert>

    <!-- Loading org spinner -->
    <div v-if="loadingUserOrganization" class="mt-3">
      <b-spinner small label="Loading organization..." />
    </div>

    <!-- No organization selected -->
    <div v-if="userStore.noOrganizationSelected && userStore.admin">
      <BAlert variant="info" class="mt-3" v-model="noMembership">
        You are not a member of any organization. Provide your email address to
        your organization admin and ask them to add you to their organization or
        create a brand new organization below since you are an admin.
      </BAlert>
      <OrganizationForm
        :headerText="'Create new organization'"
        :editable="true"
        :buttonText="'Create new organization'"
        :showAdminFields="false"
        @organizationCreated="handleOrganizationSaved"
      />
    </div>
    <div v-else-if="userStore.noOrganizationSelected">
      <p>
        You are not a member of any organization. Provide your email address to
        your organization admin and ask them to add you to their organization or
        ask your Sampling Human account manager for assistance.
      </p>
      <BAlert variant="warning" class="mt-3" v-model="noMembership">
        <h5>You are not a member of any organization.</h5>
        <p>
          Provide your email address to your organization admin and ask them to
          add you to their organization or ask your Sampling Human account
          manager for assistance.
        </p>
      </BAlert>
    </div>

    <!-- User Organizations List -->
    <div class="mt-2">
      <h4>
        <font-awesome-icon :icon="['fas', 'city']" />
        List of your Organizations
      </h4>

      <!-- Query Error -->
      <b-alert
        v-model="errorUserAssoc"
        variant="danger"
        dismissible
        class="mt-3"
      >
        {{ errorUserAssoc }}
        <b-button
          @click="requestRefetchUserAssoc"
          variant="outline-dark"
          class="btn-sm"
        >
          <font-awesome-icon :icon="['fas', 'sync']" />
          Retry</b-button
        >
      </b-alert>

      <!-- Invites where user is invited -->
      <h5 class="mt-4" v-if="inviteCountIncoming > 0">
        Your pending invitations
      </h5>
      <InviteList
        ref="incomingInvitesListRef"
        :email-pattern="userStore.email"
        @count-updated="updateNumberOfIncomingInvites($event)"
        @invites-updated="handleIncomingInvitesUpdated()"
        status-pattern="PENDING"
      />

      <!-- Loading organizations spinner -->
      <h5 class="mt-4" v-if="inviteCountIncoming > 0">Your organizations</h5>
      <div v-if="loadingUserAssoc" class="mt-3">
        <b-spinner small label="Loading organizations..." />
      </div>

      <OrganizationMembershipList
        v-if="userAssociations"
        :isLoading="loadingUserAssoc"
        :isError="errorUserAssoc !== null"
        :associations="userAssociations"
        :canManage="canEditOrg"
        :show-active-status="true"
        :showEmail="false"
        :showOrganization="true"
        :isOwner="membership === ORGANIZATION_USER_ROLES.OWNER"
        @memberships-updated="handleMembershipsUpdate"
        @row-clicked="showOrganizationDetails"
      />
      <p v-if="userAssociations && userAssociations.length === 0">
        No organizations found.
      </p>
    </div>

    <!-- If user is admin/owner => canEditOrg > can invite members => show "add member" UI and member list -->
    <div v-if="canEditOrg" class="mt-4">
      <h4>
        <font-awesome-icon :icon="['fas', 'users']" class="me-1" />
        Manage members of {{ userOrgData?.name }}
      </h4>

      <!-- Invites where user is inviter -->
      <h5 class="mt-4" v-if="inviteCountOutgoing > 0">
        Pending invitations sent by you
      </h5>
      <InviteList
        ref="outgoingInvitesListRef"
        :inviterUserId="userStore.id"
        @count-updated="updateNumberOfOutgoingInvites($event)"
        @invites-updated="handleOutgoingInvitesUpdated()"
        status-pattern="PENDING"
      />

      <!-- Invite New User Button -->
      <BButton
        type="submit"
        variant="outline-dark"
        class="mt-2"
        @click="showInviteCreate = true"
      >
        <font-awesome-icon :icon="['fas', 'user-plus']" class="me-1" />
        Invite new user
      </BButton>

      <InviteCreate
        v-if="userOrgData"
        :show="showInviteCreate"
        :organization="userOrgData"
        @success="handleOutgoingInvitesUpdated()"
        @close="showInviteCreate = false"
      />

      <!-- Organization Membership list visible to owners and admins -->
      <h5 class="mt-4" v-if="inviteCountOutgoing > 0">Organization members</h5>
      <OrganizationMembershipList
        v-if="userOrgData"
        :isLoading="loadingUserOrganization"
        :isError="errorUserOrganization !== null"
        :associations="organizationAssociations"
        :canManage="canEditOrg"
        :isOwner="membership === ORGANIZATION_USER_ROLES.OWNER"
        :show-active-status="false"
        :showEmail="true"
        :showOrganization="false"
        @memberships-updated="handleMembershipsUpdate"
        class="mt-3"
      />
      <p
        v-if="organizationAssociations && organizationAssociations.length === 0"
      >
        No organization members found.
      </p>
    </div>

    <!-- Remove Association Modal -->
    <b-modal
      v-if="selectedOrgAssoc?.organization"
      id="orgDetailsModal"
      v-model="showOrgDetails"
      hide-footer
    >
      <!-- Modal title -->
      <template #title>
        <font-awesome-icon :icon="['fas', 'building']" class="me-1" />
        {{ selectedOrgAssoc.organization.name }}
      </template>

      <!-- Modal body -->
      <OrganizationForm
        headerText=""
        :organization="selectedOrgAssoc.organization"
        :editable="
          [
            ORGANIZATION_USER_ROLES.OWNER,
            ORGANIZATION_USER_ROLES.ADMIN,
          ].includes(selectedOrgAssoc.userRole as any)
        "
        :buttonText="'Save Changes'"
        :showAdminFields="false"
        @organizationUpdated="handleOrganizationSaved"
        @close="
          showOrgDetails = false;
          selectedOrgAssoc = null;
        "
      />
    </b-modal>
  </div>
</template>
